import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  SHORTCUT_ACTIONS,
  type ShortcutBindings,
  type ShortcutGroup,
} from '@/shared/lib/shortcuts';
import { IconButton, Icons, ShortcutKeys } from '@/shared/ui';

import styles from './ShortcutHelp.module.css';

const GROUPS: readonly ShortcutGroup[] = ['navigation', 'tasks', 'search', 'general'];

interface Props {
  /** The user's CURRENT bindings (defaults + their own). */
  bindings: ShortcutBindings;
  onClose: () => void;
}

/**
 * Non-modal keyboard reference: no backdrop, no focus trap, the page stays interactive.
 * It closes on Escape (unless a dialog is open, which owns Escape) and on a press outside it;
 * the press itself is not intercepted. Opening and the `?` toggle live in the shortcut layer.
 */
export function ShortcutHelp({ bindings, onClose }: Props) {
  const { t } = useTranslation();
  const root = useRef<HTMLElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('dialog[open]')) return;
      onClose();
    };
    const onPress = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPress, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPress, true);
    };
  }, [onClose]);

  return (
    <aside
      ref={root}
      className={styles.panel}
      role="complementary"
      aria-labelledby="shortcut-help-title"
      data-testid="shortcut-help"
    >
      <div className={styles.head}>
        <h2 id="shortcut-help-title" className={styles.title}>
          {t('shortcuts.title')}
        </h2>
        <IconButton label={t('common.close')} icon={<Icons.close />} onClick={onClose} />
      </div>
      {GROUPS.map((group) => {
        const items = SHORTCUT_ACTIONS.filter((info) => info.group === group);
        const fixed = group === 'general';
        if (items.length === 0 && !fixed) return null;
        return (
          <section key={group} className={styles.group}>
            <h3 className={styles.groupTitle}>{t(`shortcuts.group.${group}`)}</h3>
            <dl className={styles.list}>
              {items.map((info) => (
                <div key={info.id} className={styles.row}>
                  <dt>{t(`shortcuts.action.${info.id}`)}</dt>
                  <dd>
                    <ShortcutKeys
                      binding={bindings[info.id]}
                      emptyLabel={t('shortcuts.unassigned')}
                      thenLabel={t('shortcuts.then')}
                    />
                  </dd>
                </div>
              ))}
              {fixed && (
                <div className={styles.row}>
                  <dt>{t('shortcuts.action.close')}</dt>
                  <dd>
                    <kbd className={styles.key}>Esc</kbd>
                  </dd>
                </div>
              )}
            </dl>
          </section>
        );
      })}
      <Link to="/app/settings/shortcuts" className={styles.configure} onClick={onClose}>
        {t('shortcuts.configure')}
      </Link>
    </aside>
  );
}
